import { ApolloClient } from '@apollo/client'
import { LocalDate } from '@js-joda/core'
import { hasCompleteInsuranceDetails } from 'common/data/hasCompleteInsuranceDetails'
import { relationshipTypeToGuardianshipStatus } from 'common/data/relationshipTypes'
import { isNil } from 'lodash'
import {
  FetchAppointmentNotification,
  GuardianshipType,
  InsuranceOptOutStatus,
  PatientRegistrationCaregiverInput,
  PatientRegistrationInput,
  RelationshipType,
  SelfRegisterPatient,
  SelfRegisterPatientVariables,
} from 'types/graphql'
import { useLocalStorage } from 'usehooks-ts'

import { FieldValues, UseFormReturn } from '@cedarjs/forms'
import { useParams } from '@cedarjs/router'
import { TypedDocumentNode } from '@cedarjs/web'

import type { PatientFacingAddressLookupContext } from 'src/components/form/PatientFacingAddressFields/PatientFacingAddressFields'
import { combineImagesInStack } from 'src/utils/combineImagesInStack'
import type { PatientFacingStepConfig } from 'src/utils/patientFacingFormSteps'

export const selfRegistrationSteps = [
  'verification',
  'are-you-the-patient',
  'patient-demographics',
  'guardianship-status',
  'caregivers-search',
  'caregivers-search-results',
  'caregiver-relationship-to-patient',
  'caregiver-is-guardian-or-guarantor',
  'patient-address',
  'dependent-patient-address',
  'patient-contact-information',
  'does-patient-have-health-insurance',
  'patient-health-insurance-type',
  'caregiver-demographics',
  'caregiver-address',
  'caregiver-contact-information',
  'add-another-caregiver',
  'primary-guardian-and-guarantor',
  'patient-health-insurance-policy-holder',
  'patient-health-insurance-details',
  'patient-health-insurance-image',
  'add-another-insurance',
] as const

export type SelfRegistrationStep = (typeof selfRegistrationSteps)[number]

export const isSelfRegistrationStep = (
  step: string
): step is SelfRegistrationStep =>
  selfRegistrationSteps.map(String).includes(step)

export const repeatingFlows = ['caregiver', 'insurance'] as const

export type RepeatingFlow = (typeof repeatingFlows)[number]

export type RepeatingFlowConfigBase = {
  name: RepeatingFlow
  position?: 'start' | 'end'
}

export type RepeatingFlowConfig =
  | RepeatingFlowConfigBase
  | ((context: {
      appointmentConfirmation: FetchAppointmentNotification['appointmentConfirmation']
    }) => RepeatingFlowConfigBase | undefined)

export interface SelfRegistrationFieldsContext {
  tenantId: string
  formData: FieldValues
  formProgress?: SelfRegistrationFormProgress
  formMethods: UseFormReturn
  addressLookupContext: PatientFacingAddressLookupContext
  minimumMinorAge: number
  repeatingFlowIndex?: number
  featureToggles?: string[]
  usesInsuranceReviewAgent: boolean
}

export type SelfRegistrationStepConfig = PatientFacingStepConfig<
  SelfRegistrationFieldsContext,
  {
    repeatingFlow?: RepeatingFlowConfig
    title:
      | string
      | ((context: {
          formProgress: SelfRegistrationFormProgress
          repeatingFlowIndex?: number
        }) => string)
    description?:
      | string
      | ((context: {
          formProgress: SelfRegistrationFormProgress
          repeatingFlowIndex?: number
        }) => string)
    next: (context: {
      notificationId: string
      patientRegistrationIntentId: string
      client: ApolloClient<object>
      formData: FieldValues
      formProgress?: SelfRegistrationFormProgress
      repeatingFlowIndex?: number
      appointmentConfirmation: FetchAppointmentNotification['appointmentConfirmation']
      minimumMinorAge: number
    }) => Promise<SelfRegistrationStep | 'sign-practice-forms' | null>
    back: (context: {
      notificationId: string
      repeatingFlowIndex?: number
      formProgress?: SelfRegistrationFormProgress
      minimumMinorAge: number
      appointmentConfirmation: NonNullable<
        FetchAppointmentNotification['appointmentConfirmation']
      >
    }) => SelfRegistrationStep | null
    navTitle: string
    savedFormDataToDefaultValues?: (data: {
      formData: FieldValues
    }) => FieldValues
    formDefaultValues?: (context: {
      client: ApolloClient<object>
      patientRegistrationIntentId: string
      patientBirthDate: string
      minimumMinorAge: number
      formProgress: SelfRegistrationFormProgress | null
      repeatingFlowIndex: number | undefined
    }) => Promise<FieldValues>
    submitButton?: {
      text?: string
      disabled?: boolean
    }
  }
>

export type SelfRegistrationConfig = {
  [page in SelfRegistrationStep]: SelfRegistrationStepConfig
}

export interface SelfRegistrationFormProgress {
  steps: {
    [step in SelfRegistrationStep]?: FieldValues
  }
}

const CURRENT_STEP_KEY_PREFIX = 'patientSelfRegistrationCurrentStep'

const currentStepKey = (notificationId: string): string =>
  [CURRENT_STEP_KEY_PREFIX, notificationId].join(':')

export type ExistingCaregiverInfo = {
  type: 'existing'
  reference: string
  relatedPerson: {
    id: string
    givenName: string
    familyName: string
    birthDate: LocalDate
    relatedPatients: {
      id: string
      givenName: string
      familyNameLastInitial: string
      birthYear: string
    }[]
    existingInsuranceCoverages: {
      id: string
      payerId: string
      payerDisplayName: string
      planMemberIdentifier: string
      beneficiaryDisplayName: string
    }[]
  }
  relationship: {
    doesResideWith?: boolean
    guardianshipType: GuardianshipType
    isGuarantor: boolean
    relationshipType: RelationshipType
  }
}[]

export const useLastSavedStep = ({
  notificationId,
}: {
  notificationId: string
}) =>
  useLocalStorage<SelfRegistrationStep | 'sign-practice-forms' | null>(
    currentStepKey(notificationId),
    null
  )

const FORM_PROGRESS_KEY_PREFIX = 'patientSelfRegistrationFormProgress'
const formProgressKey = (notificationId: string) =>
  [FORM_PROGRESS_KEY_PREFIX, notificationId].join(':')

export const useFormProgress = ({
  notificationId,
}: {
  notificationId: string
}) =>
  useLocalStorage<SelfRegistrationFormProgress | null>(
    formProgressKey(notificationId),
    null
  )

const REPEATING_FLOW_PROGRESS_KEY = 'patientSelfRegistrationRepeatingFlows'
const repeatingFlowProgressKey = (notificationId: string) =>
  [REPEATING_FLOW_PROGRESS_KEY, notificationId].join(':')

export const useRepeatingFlowProgress = ({
  notificationId,
}: {
  notificationId: string
}) => {
  const [repeatingFlowProgress, saveRepeatingFlowProgress] = useLocalStorage<{
    [flow in RepeatingFlow]: { currentIndex: number }
  }>(repeatingFlowProgressKey(notificationId), {
    caregiver: { currentIndex: 0 },
    insurance: { currentIndex: 0 },
  })

  const updateFlowProgress = (amount: number) => (flowName: RepeatingFlow) =>
    saveRepeatingFlowProgress((currentState) => ({
      ...currentState,
      [flowName]: {
        currentIndex: currentState[flowName].currentIndex + amount,
      },
    }))
  const incrementProgress = updateFlowProgress(1)
  const decrementProgress = updateFlowProgress(-1)

  return [
    repeatingFlowProgress,
    { incrementProgress, decrementProgress },
  ] as const
}

export const useCurrentStep = (): SelfRegistrationStep | null => {
  const { step } = useParams()

  if (!isSelfRegistrationStep(step)) return null

  return step
}

export const repeatingFlowFormProgressToArray = (
  formProgress: FieldValues
): FieldValues[] =>
  Object.entries(formProgress ?? {})
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([_, formData]) => formData)

type NewCaregiver = NonNullable<
  PatientRegistrationCaregiverInput['newCaregivers']
>[number] & {
  type: 'new'
  birthDate: LocalDate
}

export const getCaregiversFromFormProgress = (
  formProgress: SelfRegistrationFormProgress | null | undefined
): NewCaregiver[] => {
  if (!formProgress?.steps?.['add-another-caregiver']) return []

  const addAnotherCaregiverAnswers = repeatingFlowFormProgressToArray(
    formProgress?.steps?.['add-another-caregiver']
  )

  const caregiverCount =
    addAnotherCaregiverAnswers.findIndex(
      (formData) => formData.addAnother === 'no'
    ) + 1

  const caregiverInfo: NewCaregiver[] = new Array(caregiverCount)
    .fill('')
    .flatMap((_, i) => {
      const isExistingCaregiver = determineIfIsExistingCaregiver(
        formProgress,
        i
      )

      if (isExistingCaregiver) return []

      const relationship =
        formProgress?.steps?.['caregiver-relationship-to-patient']?.[i]
      const demographics = formProgress?.steps?.['caregiver-demographics']?.[i]
      const address = formProgress?.steps?.['caregiver-address']?.[i]
      const contactInformation =
        formProgress?.steps?.['caregiver-contact-information']?.[i]
      const primaryGuardianAndGuarantor =
        formProgress?.steps?.['primary-guardian-and-guarantor']
      const patientAddress = formProgress?.steps?.['dependent-patient-address']
      // caregiver-is-guardian-or-guarantor is a repeating-flow step, so its
      // answers are saved indexed by caregiver
      const isGuardianOrGuarantor =
        formProgress?.steps?.['caregiver-is-guardian-or-guarantor']?.[i]
      const reference = String(i)
      return {
        type: 'new',
        reference,
        givenName: demographics.givenName,
        familyName: demographics.familyName,
        birthDate: demographics.birthDate,
        maritalStatus: demographics.maritalStatus,
        occupation: demographics.occupation,
        contactInformation: {
          emailAddress: contactInformation.emailAddress,
          mobileNumber: contactInformation.mobilePhoneNumber,
          homeNumber: contactInformation.homePhoneNumber,
          workNumber: contactInformation.workPhoneNumber,
          homeAddress: address.homeAddress,
          useHomeAddressAsMailingAddress:
            address.useHomeAddressAsMailingAddress,
          mailingAddress: !address.useHomeAddressAsMailingAddress
            ? address.mailingAddress
            : undefined,
          primaryLanguage: demographics.primaryLanguage,
        },
        relationship: {
          guardianshipType:
            isGuardianOrGuarantor?.isGuardian === 'yes' ||
            primaryGuardianAndGuarantor?.primaryGuardian === reference
              ? relationshipTypeToGuardianshipStatus[
                  relationship.relationshipType as RelationshipType
                ]
              : 'NON_GUARDIAN',
          relationshipType: relationship.relationshipType,
          isGuarantor:
            isGuardianOrGuarantor?.isGuarantor === 'yes' ||
            primaryGuardianAndGuarantor?.primaryGuarantor === reference,
          doesResideWith:
            !!patientAddress?.livesWithCaregivers?.includes(reference),
        },
      }
    })
  return caregiverInfo
}

export const getExistingCaregiversFromFormProgress = (
  formProgress: SelfRegistrationFormProgress | null | undefined
): NonNullable<ExistingCaregiverInfo> => {
  if (!formProgress?.steps?.['add-another-caregiver']) return []

  const addAnotherCaregiverAnswers = repeatingFlowFormProgressToArray(
    formProgress?.steps?.['add-another-caregiver']
  )

  const caregiverCount =
    addAnotherCaregiverAnswers.findIndex(
      (formData) => formData.addAnother === 'no'
    ) + 1

  const existingCaregiverInfo: ExistingCaregiverInfo = new Array(caregiverCount)
    .fill('')
    .flatMap((_, i) => {
      const isExistingCaregiver = determineIfIsExistingCaregiver(
        formProgress,
        i
      )

      if (!isExistingCaregiver) return []

      const caregiver =
        formProgress.steps['caregivers-search-results']?.[i].relatedPerson
      const caregiverInfo = JSON.parse(caregiver)
      const relationship =
        formProgress?.steps?.['caregiver-relationship-to-patient']?.[i]
      const patientAddress = formProgress?.steps?.['dependent-patient-address']
      // caregiver-is-guardian-or-guarantor is a repeating-flow step, so its
      // answers are saved indexed by caregiver
      const isGuardianOrGuarantor =
        formProgress?.steps?.['caregiver-is-guardian-or-guarantor']?.[i]
      const primaryGuardianAndGuarantor =
        formProgress?.steps?.['primary-guardian-and-guarantor']

      const reference = caregiverInfo.id

      return {
        type: 'existing',
        reference,
        relatedPerson: caregiverInfo,
        relationship: {
          guardianshipType:
            isGuardianOrGuarantor?.isGuardian === 'yes' ||
            primaryGuardianAndGuarantor?.primaryGuardian === reference
              ? relationshipTypeToGuardianshipStatus[
                  relationship.relationshipType as RelationshipType
                ]
              : 'NON_GUARDIAN',
          relationshipType: relationship.relationshipType,
          isGuarantor:
            isGuardianOrGuarantor?.isGuarantor === 'yes' ||
            primaryGuardianAndGuarantor?.primaryGuarantor === reference,
          doesResideWith:
            !!patientAddress?.livesWithCaregivers?.includes(reference),
        },
      }
    })
  return existingCaregiverInfo
}

export type RegistrationInsurance = Pick<
  NonNullable<PatientRegistrationInput['insurance']>,
  'insurances'
>

export class RegistrationInsuranceInputError extends Error {}

export const getRegistrationInsuranceFromFormProgress = async ({
  formProgress,
  repeatingFlowIndex,
  usesInsuranceReviewAgent,
}: {
  formProgress: SelfRegistrationFormProgress
  repeatingFlowIndex?: number
  usesInsuranceReviewAgent: boolean
}): Promise<RegistrationInsurance> => {
  const insurances: NonNullable<RegistrationInsurance['insurances']> = []
  const result = { insurances }

  if (
    formProgress.steps['does-patient-have-health-insurance']
      ?.doesPatientHaveInsurance !== 'yes'
  ) {
    return result
  }

  for (let i = 0; i <= (repeatingFlowIndex ?? 0); i++) {
    const insuranceDetails =
      formProgress.steps['patient-health-insurance-details']?.[i]
    const cardImagesStep =
      formProgress.steps['patient-health-insurance-image']?.[i]
    const coordinationOfBenefitsType =
      formProgress.steps['patient-health-insurance-type']?.[i]
        ?.coordinationOfBenefitsType

    // Browser back can abandon an additional plan without decrementing the flow index.
    if (
      !insuranceDetails ||
      (usesInsuranceReviewAgent ? !cardImagesStep : !coordinationOfBenefitsType)
    ) {
      continue
    }
    if (usesInsuranceReviewAgent && !insuranceDetails.insuranceProvider) {
      throw new RegistrationInsuranceInputError('Choose an insurance provider')
    }

    const cardImageUrls = [
      cardImagesStep?.insuranceBinaries?.front?.url,
      cardImagesStep?.insuranceBinaries?.back?.url,
    ].filter((url): url is string => Boolean(url))
    const card = cardImageUrls.length
      ? await combineImagesInStack({
          imageUrls: cardImageUrls,
          skipUnavailableImages: usesInsuranceReviewAgent,
        }).catch(() => undefined)
      : undefined
    const insuranceCardBinary = card
      ? { content: card.content, contentType: card.contentType }
      : undefined

    const policyHolder =
      formProgress.steps['patient-health-insurance-policy-holder']?.[i]
        ?.policyHolder
    const insurance = {
      payerId: insuranceDetails.insuranceProvider,
      subscriberRef: policyHolder !== 'patient' ? policyHolder : undefined,
      beneficiaryIsSubscriber: policyHolder
        ? policyHolder === 'patient'
        : undefined,
      planMemberIdentifier: insuranceDetails.memberId || undefined,
      coordinationOfBenefitsType,
      insuranceCardBinary,
      status: 'ACTIVE' as const,
    }
    if (
      !hasCompleteInsuranceDetails(insurance) &&
      !(usesInsuranceReviewAgent && insuranceCardBinary)
    ) {
      throw new RegistrationInsuranceInputError(
        cardImageUrls.length
          ? `Insurance card for plan ${i + 1} is unavailable. Go back and upload it again.`
          : 'Insurance details are required without an insurance card'
      )
    }
    insurances.push(insurance)
  }

  return result
}

export const userIsThePatient = ({
  formProgress,
}: {
  formProgress: SelfRegistrationFormProgress
}) => formProgress.steps?.['are-you-the-patient']?.isThePatient === 'yes'

// When a caregiver registers a dependent, the caregiver at repeating-flow
// index 0 is the person filling out the form — they are the one who can sign
// practice forms. They may be either a new caregiver or an existing one
// selected via caregiver search, so pick from the right list based on what
// index 0 actually is.
export const getPrimaryCaregiverForSigning = ({
  formProgress,
}: {
  formProgress: SelfRegistrationFormProgress
}) => {
  const newCaregivers = getCaregiversFromFormProgress(formProgress)
  const existingCaregivers = getExistingCaregiversFromFormProgress(formProgress)

  const caregiverUser = determineIfIsExistingCaregiver(formProgress, 0)
    ? existingCaregivers[0]
    : newCaregivers[0]

  if (!caregiverUser) return null

  return caregiverUser.type === 'existing'
    ? {
        display: caregiverUser.relatedPerson,
        relationship: caregiverUser.relationship,
        reference: caregiverUser.reference,
      }
    : {
        display: caregiverUser,
        relationship: caregiverUser.relationship,
        reference: caregiverUser.reference,
      }
}

export const userCanSignPracticeForms = ({
  formProgress,
}: {
  formProgress: SelfRegistrationFormProgress
}): boolean => {
  const guardianshipStatus =
    formProgress.steps['guardianship-status']?.guardianshipStatus

  const caregiverUser = getPrimaryCaregiverForSigning({ formProgress })

  return userIsThePatient({ formProgress })
    ? guardianshipStatus === 'INDEPENDENT_ADULT' ||
        guardianshipStatus === 'EMANCIPATED_MINOR'
    : caregiverUser?.relationship?.guardianshipType === 'LEGAL' ||
        caregiverUser?.relationship?.guardianshipType === 'NATURAL'
}

export const isEnteringOwnCaregiverInformation = ({
  formProgress,
  repeatingFlowIndex,
}: {
  formProgress: SelfRegistrationFormProgress
  repeatingFlowIndex?: number
}) => !userIsThePatient({ formProgress }) && repeatingFlowIndex === 0

export const convertFormProgressToRegistrationInput = ({
  patientRegistrationIntentId,
  formProgress,
  insurance,
  insuranceOptOutStatus,
}: {
  patientRegistrationIntentId: string
  formProgress: SelfRegistrationFormProgress
  insurance: RegistrationInsurance
  insuranceOptOutStatus?: InsuranceOptOutStatus
}): PatientRegistrationInput => {
  const isThePatient = userIsThePatient({ formProgress })
  const demographics = formProgress.steps['patient-demographics']
  const contactInformation = formProgress.steps['patient-contact-information']
  const address =
    formProgress.steps['patient-address'] ??
    formProgress.steps['dependent-patient-address']
  const guardianshipStatus = formProgress.steps['guardianship-status']
  const existingCaregivers = getExistingCaregiversFromFormProgress(formProgress)
  const existingCaregiversInput = existingCaregivers.map(
    (existingCaregiver) => {
      return {
        reference: existingCaregiver.reference,
        relatedPersonId: existingCaregiver.relatedPerson.id,
        relationship: existingCaregiver.relationship,
      }
    }
  )
  const caregivers = getCaregiversFromFormProgress(formProgress)
  const caregiversInput = caregivers.map(({ type: _type, ...rest }) => rest)
  const primaryGuardianAndGuarantor =
    formProgress.steps['primary-guardian-and-guarantor']
  const livesWithCaregiverReferences = address?.livesWithCaregivers ?? []
  const allCaregivers = [...existingCaregivers, ...caregivers]
  const livesWithCaregiver = allCaregivers.find((caregiver) =>
    livesWithCaregiverReferences.includes(caregiver.reference)
  )

  const getAddress = () => {
    if (livesWithCaregiver?.type === 'new') {
      return livesWithCaregiver.contactInformation?.homeAddress
    }

    if (address?.homeAddress) {
      if (
        address.homeAddress.line1 &&
        address.homeAddress.city &&
        address.homeAddress.state &&
        address.homeAddress.postalCode
      )
        return address.homeAddress
    }

    return undefined
  }

  const getMailingAddress = () => {
    if (livesWithCaregiver?.type === 'new') {
      return livesWithCaregiver.contactInformation?.mailingAddress
    }

    if (address?.mailingAddress) {
      if (
        address.mailingAddress.line1 &&
        address.mailingAddress.city &&
        address.mailingAddress.state &&
        address.mailingAddress.postalCode
      ) {
        return address.mailingAddress
      }
    }
    return undefined
  }
  const patientHomeAddress = getAddress()
  const patientMailingAddress = getMailingAddress()

  return {
    patientRegistrationIntentId,
    patient: {
      givenName: demographics!.givenName,
      familyName: demographics!.familyName,
      preferredName: demographics!.preferredName,
      birthDate: demographics!.birthDate,
      sexAtBirth: demographics!.sexAtBirth,
      motherMaidenName: demographics!.motherMaidenName,
      primaryGuardianReference: primaryGuardianAndGuarantor?.primaryGuardian,
      primaryGuarantorReference: primaryGuardianAndGuarantor?.primaryGuarantor,
      contactInformationV2: contactInformation?.mobilePhoneNumber
        ? {
            emailAddress: contactInformation.emailAddress,
            mobileNumber: contactInformation.mobilePhoneNumber,
            homeNumber: contactInformation.homePhoneNumber,
            homeAddress: patientHomeAddress,
            useHomeAddressAsMailingAddress:
              address!.useHomeAddressAsMailingAddress,
            mailingAddress: !address!.useHomeAddressAsMailingAddress
              ? patientMailingAddress
              : undefined,
            primaryLanguage: demographics!.primaryLanguage,
          }
        : undefined,
    },
    caregiver: {
      caregiverOptOut: !caregivers?.length && !existingCaregivers?.length,
      legalStatus: !isThePatient
        ? 'DEPENDENT'
        : guardianshipStatus?.guardianshipStatus,
      newCaregivers: caregiversInput,
      existingCaregivers: existingCaregiversInput,
    },
    insurance: {
      insuranceOptOut: !insurance.insurances?.length,
      insuranceOptOutStatus: insuranceOptOutStatus,
      insurances: insurance.insurances,
    },
  }
}

export const getRepeatingFlowConfig = ({
  stepConfig,
  context,
}: {
  stepConfig: SelfRegistrationStepConfig
  context: {
    appointmentConfirmation: FetchAppointmentNotification['appointmentConfirmation']
  }
}): RepeatingFlowConfigBase | undefined =>
  typeof stepConfig?.repeatingFlow === 'function'
    ? stepConfig.repeatingFlow(context)
    : stepConfig?.repeatingFlow

export const determineIfIsExistingCaregiver = (
  formProgress: SelfRegistrationFormProgress | undefined,
  index: number | undefined
): boolean => {
  if (!formProgress) return false

  if (isNil(index)) return false

  const isNewCaregiver =
    !formProgress.steps?.['caregivers-search-results']?.[index]

  const selectedAddANewCaregiver =
    formProgress.steps?.['caregivers-search-results']?.[index]
      ?.relatedPerson === 'addNewRelatedPerson'

  return !isNewCaregiver && !selectedAddANewCaregiver
}

export const SELF_REGISTER_PATIENT: TypedDocumentNode<
  SelfRegisterPatient,
  SelfRegisterPatientVariables
> = gql`
  mutation SelfRegisterPatient(
    $input: PatientRegistrationInput!
    $registrationIntentBirthDate: Date!
    $appointmentNotificationId: String!
    $practiceForms: SelfRegistrationPracticeFormsInput
  ) {
    selfRegisterPatient(
      input: $input
      registrationIntentBirthDate: $registrationIntentBirthDate
      appointmentNotificationId: $appointmentNotificationId
      practiceForms: $practiceForms
    ) {
      id
    }
  }
`
